/**
 * AuthContext — global auth state using Supabase onAuthStateChange.
 *
 * Bootstrap logging reference (grep [Nudge] in console):
 *   [Nudge/auth] bootstrap start
 *   [Nudge/auth] INITIAL_SESSION received  — user: <id|null>
 *   [Nudge/auth] loadProfile start         — userId: <id>
 *   [Nudge/auth] loadProfile success       — <display_name>
 *   [Nudge/auth] loadProfile timeout       — after 5 s
 *   [Nudge/auth] loadProfile error         — <message>
 *   [Nudge/auth] auth init timeout         — 8 s safety net fired (D-029)
 *   [Nudge/auth] signIn start / success / error
 */
import {
  createContext, useContext, useEffect, useRef, useState,
  useCallback, useMemo, type ReactNode,
} from 'react'
import type { User, Session } from '@supabase/supabase-js'
import * as Sentry from '@sentry/react'
import { supabase } from '@/lib/supabase'
import { identifyUser, resetAnalyticsUser } from '@/lib/analytics'
import type { Profile } from '@/types'

// ── Constants ─────────────────────────────────────────────────────────────────

// Auth init watchdog — fires if onAuthStateChange never delivers INITIAL_SESSION.
// 20 s gives Supabase time to refresh an expired token over a slow connection
// (token refresh is a network round-trip; 10 s was too short on cold Vercel edge).
// On timeout: FAIL SOFT — unblock the UI, preserve localStorage, report to
// Sentry (see authInitTimeout handler below). Never wipes a possibly-valid
// session on a timing signal alone (June 12, 2026).
const AUTH_INIT_TIMEOUT_MS     = 20_000
const PROFILE_FETCH_TIMEOUT_MS = 15_000

// ── Types ─────────────────────────────────────────────────────────────────────

interface AuthContextValue {
  user:               User | null
  session:            Session | null
  profile:            Profile | null
  loading:            boolean
  profileLoading:     boolean   // true while loadProfile is in-flight
  profileFetchFailed: boolean   // true when loadProfile timed out or errored
  signIn:             (email: string, password: string) => Promise<{ error: string | null }>
  signUp:             (email: string, password: string, displayName: string, opts?: { redirectTo?: string; role?: 'parent' | 'student' }) => Promise<{ error: string | null }>
  signInWithGoogle:   (returnTo?: string, signupRole?: string) => Promise<void>
  signOut:            () => Promise<void>
  sendPasswordReset:  (email: string) => Promise<{ error: string | null }>
  updatePassword:     (newPassword: string) => Promise<{ error: string | null }>
  refreshProfile:     () => Promise<void>
  retryProfileLoad:   () => Promise<void>   // call from recovery UI
}

// ── Context ───────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null)

// ── Provider ──────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user,               setUser]               = useState<User | null>(null)
  const [session,            setSession]            = useState<Session | null>(null)
  const [profile,            setProfile]            = useState<Profile | null>(null)
  const [loading,            setLoading]            = useState(true)
  const [profileLoading,     setProfileLoading]     = useState(false)
  const [profileFetchFailed, setProfileFetchFailed] = useState(false)

  // Ref to prevent concurrent loadProfile calls (e.g. when D-025 lock steal
  // causes onAuthStateChange to fire twice in rapid succession).
  const profileLoadingRef = useRef(false)

  // On boot with an existing session, Supabase fires SIGNED_IN *before*
  // INITIAL_SESSION (token refresh race). The DB isn't ready at SIGNED_IN time,
  // so every profile fetch times out for 25 s before INITIAL_SESSION fires and
  // succeeds immediately. Fix: defer loadProfile when SIGNED_IN arrives before
  // INITIAL_SESSION; let INITIAL_SESSION own the first profile fetch.
  const initialSessionFired = useRef(false)

  // Supabase's _recoverAndRefresh (token refresh) fires SIGNED_IN even when
  // a profile is already loaded. The DB fetch blocks behind the navigator.lock
  // held during token refresh → times out → recovery UI mid-session. Fix: only
  // call loadProfile on SIGNED_IN when no profile is loaded yet (fresh login).
  // Must be a ref to avoid stale closure inside the onAuthStateChange callback.
  const hasProfileRef = useRef(false)

  // ── loadProfile — with hard timeout ────────────────────────────────────────
  const loadProfile = useCallback(async (userId: string) => {
    if (profileLoadingRef.current) {
      console.log('[Nudge/auth] loadProfile: already in flight — skipping duplicate call')
      return
    }
    profileLoadingRef.current = true
    console.log('[Nudge/auth] loadProfile start — userId:', userId)
    setProfileLoading(true)
    setProfileFetchFailed(false)

    type Result =
      | { timedOut: true }
      | { timedOut: false; data: Profile | null; error: { message: string } | null }

    try {
      const result = await Promise.race<Result>([
        supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single()
          .then(({ data, error }) => ({
            timedOut: false as const,
            data:     data as Profile | null,
            error:    error as { message: string } | null,
          })),
        new Promise<Result>((resolve) =>
          setTimeout(() => resolve({ timedOut: true }), PROFILE_FETCH_TIMEOUT_MS)
        ),
      ])

      if (result.timedOut) {
        // Profile DB fetch timed out — auto-retry once silently before surfacing the error UI.
        // This handles transient Supabase edge cold-starts after a page reload.
        console.warn('[Nudge/auth] loadProfile timeout — silently retrying once')
        const retryResult = await Promise.race<Result>([
          supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .single()
            .then(({ data, error }) => ({
              timedOut: false as const,
              data:     data as Profile | null,
              error:    error as { message: string } | null,
            })),
          new Promise<Result>((resolve) =>
            setTimeout(() => resolve({ timedOut: true }), 10_000)
          ),
        ])
        if (!retryResult.timedOut && !retryResult.error && retryResult.data) {
          console.log('[Nudge/auth] loadProfile retry success —', retryResult.data.display_name ?? 'no name')
          hasProfileRef.current = true
          setProfile(retryResult.data)
          setProfileFetchFailed(false)
          return
        }
        console.warn('[Nudge/auth] loadProfile retry also failed — showing recovery UI, session preserved')
        setProfileFetchFailed(true)
        return
      }

      if (result.error) {
        console.warn('[Nudge/auth] loadProfile error:', result.error.message)
        setProfileFetchFailed(true)
        return
      }

      console.log('[Nudge/auth] loadProfile success —', result.data?.display_name ?? 'no name')
      hasProfileRef.current = true
      setProfile(result.data)
      setProfileFetchFailed(false)
      // Identify user in PostHog — students silently suppressed (COPPA child exclusion)
      if (result.data?.role === 'parent' || result.data?.role === 'student') {
        identifyUser(userId, result.data.role)
      }
    } catch (err) {
      console.warn('[Nudge/auth] loadProfile exception:', err)
      setProfileFetchFailed(true)
    } finally {
      setProfileLoading(false)
      profileLoadingRef.current = false
    }
  }, [])

  const refreshProfile = useCallback(async () => {
    if (user) await loadProfile(user.id)
  }, [user, loadProfile])

  const retryProfileLoad = useCallback(async () => {
    if (user) await loadProfile(user.id)
  }, [user, loadProfile])

  // ── Bootstrap — onAuthStateChange ──────────────────────────────────────────
  useEffect(() => {
    let mounted = true
    console.log('[Nudge/auth] bootstrap start')

    // Safety net: if INITIAL_SESSION never fires (stale SW lock, navigator.locks
    // held by previous tab, corrupted IndexedDB token), unblock the UI after
    // 20 s and let ProtectedRoute redirect to /login.
    //
    // FAIL SOFT — never wipe storage here (changed June 12, 2026). A timeout is
    // a signal of SLOWNESS, not of token invalidity: on a slow network or cold
    // edge, INITIAL_SESSION can arrive at 25 s with a perfectly valid session.
    // The old clearAndRedirect here destroyed that session — for a managed
    // child (no password, single-use login links) that meant being locked out
    // entirely. With storage preserved, a reload or revisit recovers the
    // session; if the token is GENUINELY corrupted (original D-025 stale-lock
    // scenario), the user lands on /login where the LoadingScreen recovery UI
    // still offers the explicit "Sign out and start over" wipe as the manual
    // escape hatch.
    const authInitTimeout = setTimeout(() => {
      if (!mounted) return
      const hasStoredToken = Object.keys(localStorage).some(
        (k) => k.startsWith('sb-') && k.endsWith('-auth-token')
      )
      console.warn(
        `[Nudge/auth] auth init timeout — failing soft, preserving localStorage (token ${hasStoredToken ? 'present' : 'absent'}, tab ${document.hidden ? 'hidden' : 'visible'})`
      )
      // Frequency visibility in production — this firing at scale means a
      // Supabase availability or stale-lock problem worth investigating.
      Sentry.captureMessage('auth_init_timeout', {
        level: 'warning',
        tags:  { token_present: String(hasStoredToken), tab_hidden: String(document.hidden) },
      })
      setLoading(false)
    }, AUTH_INIT_TIMEOUT_MS)

    // Do NOT call getSession() here — it races with onAuthStateChange for the
    // same navigator.locks browser lock ("lock stolen" errors — Sentry D-025).
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return
        clearTimeout(authInitTimeout)

        console.log(`[Nudge/auth] onAuthStateChange: ${event} — user: ${session?.user?.id ?? 'null'}`)

        setSession(session)
        setUser(session?.user ?? null)

        if (session?.user) {
          // Unblock UI immediately — do NOT wait for profile DB query (D-029).
          // ProtectedRoute's profileLoading gate covers the profile-fetch phase.
          setLoading(false)

          if (event === 'INITIAL_SESSION') {
            initialSessionFired.current = true
            await loadProfile(session.user.id)
          } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
            if (!initialSessionFired.current) {
              // Early-boot SIGNED_IN or TOKEN_REFRESHED fires before
              // INITIAL_SESSION. TOKEN_REFRESHED is what fires when the stored
              // access token is already expired on boot (any return visit after
              // ~1 h idle). The Supabase DB connection isn't ready yet — any
              // profile fetch would time out. Keep profileLoading = true so
              // ProtectedRoute shows a loading screen; INITIAL_SESSION will
              // fetch the profile.
              //
              // June 12, 2026 (production journey simulation): TOKEN_REFRESHED
              // was unhandled here, so for one render the context exposed
              // user-set + profileLoading=false + profile=null — ProtectedRoute
              // Gate 1d read that as "no profile row" and bounced a perfectly
              // valid session to /login. THE root cause of "keeps getting
              // logged out".
              setProfileLoading(true)
            } else if (!hasProfileRef.current) {
              // Post-boot fresh login — no profile yet, load it now.
              await loadProfile(session.user.id)
            }
            // else: SIGNED_IN/TOKEN_REFRESHED from _recoverAndRefresh (token
            // refresh / session recovery). Profile already loaded. DB blocks
            // behind navigator.lock during token refresh — never call
            // loadProfile here or it times out.
          }
        } else {
          if (event === 'INITIAL_SESSION') initialSessionFired.current = true
          hasProfileRef.current = false
          setProfile(null)
          setProfileFetchFailed(false)
          setLoading(false)
          resetAnalyticsUser()  // Clear PostHog identity on sign-out
        }
      }
    )

    return () => {
      mounted = false
      clearTimeout(authInitTimeout)
      subscription.unsubscribe()
    }
  }, [loadProfile])

  // ── Auth operations ────────────────────────────────────────────────────────

  const signIn = useCallback(async (email: string, password: string) => {
    console.log('[Nudge/auth] signIn start')
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      console.warn('[Nudge/auth] signIn error:', error.message)
      return { error: mapAuthError(error.message) }
    }
    console.log('[Nudge/auth] signIn success')
    return { error: null }
  }, [])

  const signInWithGoogle = useCallback(async (returnTo?: string, signupRole?: string) => {
    const callbackUrl = new URL(`${window.location.origin}/auth/callback`)
    if (returnTo) callbackUrl.searchParams.set('returnTo', returnTo)
    if (signupRole) callbackUrl.searchParams.set('signup_role', signupRole)
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: callbackUrl.toString(),
        queryParams: { prompt: 'select_account' },
      },
    })
  }, [])

  const signUp = useCallback(async (
    email: string, password: string, displayName: string,
    opts?: { redirectTo?: string; role?: 'parent' | 'student' }
  ) => {
    const role            = opts?.role ?? 'parent'
    const emailRedirectTo = opts?.redirectTo ?? `${window.location.origin}/auth/callback`
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { role, display_name: displayName },
        emailRedirectTo,
      },
    })
    if (error) {
      // HTTP 429 = Supabase email send rate limit exceeded (free-tier project cap,
      // not a per-user "too many attempts" issue — don't blame the user).
      if ('status' in error && (error as { status?: number }).status === 429) {
        return { error: 'Signup is temporarily unavailable due to high demand. Please try again in a few minutes.' }
      }
      return { error: mapAuthError(error.message) }
    }
    return { error: null }
  }, [])

  const signOut = useCallback(async () => {
    clearGeminiSessionCache()
    await supabase.auth.signOut()
    hasProfileRef.current = false
    setProfile(null)
    setProfileFetchFailed(false)
    console.log('[Nudge/auth] signOut complete')
  }, [])

  const sendPasswordReset = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    })
    return { error: error ? mapAuthError(error.message) : null }
  }, [])

  const updatePassword = useCallback(async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    return { error: error ? mapAuthError(error.message) : null }
  }, [])

  // ── Memoized context value ─────────────────────────────────────────────────

  const value = useMemo<AuthContextValue>(() => ({
    user, session, profile, loading, profileLoading, profileFetchFailed,
    signIn, signUp, signInWithGoogle, signOut,
    sendPasswordReset, updatePassword, refreshProfile, retryProfileLoad,
  }), [
    user, session, profile, loading, profileLoading, profileFetchFailed,
    signIn, signUp, signInWithGoogle, signOut,
    sendPasswordReset, updatePassword, refreshProfile, retryProfileLoad,
  ])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error(
    '[Nudge] useAuth must be used inside <AuthProvider>. ' +
    'Wrap your app root — see state-managementSKILL.md.'
  )
  return ctx
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function clearGeminiSessionCache(): void {
  const keys = Object.keys(sessionStorage).filter(k => k.startsWith('nudge:q:'))
  keys.forEach(k => sessionStorage.removeItem(k))
  sessionStorage.removeItem('nudge:gemini:callCount')
}

function mapAuthError(message: string): string {
  if (message.includes('Invalid login credentials'))   return 'Incorrect email or password. Please try again.'
  if (message.includes('Email not confirmed'))          return 'Please check your email and click the confirmation link first.'
  if (message.includes('User already registered'))      return 'An account with this email already exists. Try signing in.'
  if (message.includes('Password should be at least')) return 'Password must be at least 6 characters.'
  if (message.includes('over_email_send_rate_limit') ||
      message.includes('email rate limit') ||
      message.includes('rate limit exceeded'))           return 'Signup is temporarily unavailable due to high demand. Please try again in a few minutes.'
  if (message.includes('rate limit'))                   return 'Too many attempts. Please wait a moment and try again.'
  if (message.includes('network'))                      return 'Connection error. Please check your internet and try again.'
  return 'Something went wrong. Please try again.'
}
